
import { defineComponent, ref, computed } from "vue";
import OrderHeaderChangeActions from "@/components/OrderHeaderChangeActions.vue";
import BackAndCancelFooter from "@/components/BackAndCancelFooter.vue";
import ViewHeader from "@/components/ViewHeader.vue";
import { useStore } from "@/store";
import { useRouter } from "vue-router";
import { api } from "@/services/Api";
import swal from "sweetalert2";
import q from "q";
import moment from "moment";
import { useI18n } from "vue-i18n";
import { BusinessClient } from "@/models/Order";

export default defineComponent({
  props: {
    productId: {
      type: Number,
      required: true,
    },
  },
  components: {
    OrderHeaderChangeActions,
    BackAndCancelFooter,
    ViewHeader,
  },
  async setup(props) {
    const { t } = useI18n();
    const store = useStore();
    const router = useRouter();
    const clients = ref<BusinessClient[]>([]);
    const searchText = ref("");
    swal.showLoading();
    const apiPromise = api.ClubVoucherList();
    await q.delay(400);
    const apiResult = await apiPromise;
    const businessClientExist = computed(
      () => store.state.order.businessClient != null
    );
    const selectedProduct = computed(() => store.state.selectedProduct);
    if (apiResult.data) {
      clients.value = apiResult.data;
      swal.close();
    }
    const clientsToShow = computed(() => {
      if (searchText.value.length < 2) {
        return clients.value;
      }
      return clients.value.filter((x) =>
        x.name.toLowerCase().includes(searchText.value.toLowerCase())
      );
    });
    async function setClient(client: BusinessClient) {
      const date = selectedProduct.value?.date ?? moment().format("YYYY-MM-DD");
      const loadPricesResult = await api.loadPricesByBusinessClient(date, client.id);
      store.commit("updatePreloadPrices", loadPricesResult.data);

      if (store.state.order.businessClientId == client.id) {
        router.push({
          name: "productPriceLines",
          params: {
            productId: props.productId,
            slotId: selectedProduct.value?.slotId,
            dateStart: date,
          },
        });
        return;
      }
      if (store.state.order.businessClientId) {
        // changed to another Business Client
        const someProductContainsCoupon = store.state.order.products.some(
          (x) => x.priceLine.couponId
        );
        if (someProductContainsCoupon) {
          const result = await swal.fire({
            text: t("cashier.alert.differen-current-order"),
            icon: "warning",
            iconColor: "",
            showCancelButton: true,
            cancelButtonText: t("cashier.label.no"),
            confirmButtonText: t("cashier.label.yes"),
            customClass: {
              confirmButton: "popup-bookit-button",
              cancelButton: "close-button-wrapper popup-bookit-button",
            },
            buttonsStyling: false,
          });
          if (result.isConfirmed) {
            await store.dispatch("setOrderBusinessClient", client);
          }
          router.push({
            name: "productPriceLines",
            params: {
              productId: props.productId,
              slotId: selectedProduct.value?.slotId,
              dateStart: date,
            },
          });
          return;
        }
      }
      const contains = await store.dispatch(
        "orderProductsContainsCoupons",
        client
      );

      if (!contains) {
        await store.dispatch("setOrderBusinessClient", client);
        router.push({
          name: "productPriceLines",
          params: {
            productId: props.productId,
            slotId: selectedProduct.value?.slotId,
            dateStart: date,
          },
        });
        return;
      }
      const result = await swal.fire({
        text: t("cashier.alert.differen-current-order"),
        icon: "warning",
        iconColor: "",
        showCancelButton: true,
        cancelButtonText: t("cashier.label.no"),
        confirmButtonText: t("cashier.label.yes"),
        customClass: {
          confirmButton: "popup-bookit-button",
          cancelButton: "close-button-wrapper popup-bookit-button",
        },
        buttonsStyling: false,
      });
      if (!result.isConfirmed) {
        router.push({
          name: "productPriceLines",
          params: {
            productId: props.productId,
            slotId: selectedProduct.value?.slotId,
            dateStart: date,
          },
        });
        return;
      }

      await store.dispatch("setOrderBusinessClient", client);
      router.push({
        name: "productPriceLines",
        params: {
          productId: props.productId,
          slotId: selectedProduct.value?.slotId,
          dateStart: date,
        },
      });
    }
    async function setClientOldVersion(client: BusinessClient) {
      if (store.state.order.businessClientId == client.id) {
        router.push({
          name: "productPriceLines",
          params: {
            productId: props.productId,
            slotId: selectedProduct.value?.slotId,
            dateStart:
              selectedProduct.value?.date ?? moment().format("YYYY-MM-DD"),
          },
        });
        return;
      }
      if (businessClientExist.value) {
        const result = await swal.fire({
          text: "You changed the Business Client, all the discount values will be recalculated. Continue?",
          icon: "warning",
          iconColor: "",
          showCancelButton: true,
          cancelButtonText: t("cashier.label.no"),
          confirmButtonText: t("cashier.label.yes"),
          customClass: {
            confirmButton: "popup-bookit-button",
            cancelButton: "close-button-wrapper popup-bookit-button",
          },
          buttonsStyling: false,
        });
        if (!result.isConfirmed) {
          router.push({
            name: "productPriceLines",
            params: {
              productId: props.productId,
              slotId: selectedProduct.value?.slotId,
              dateStart:
                selectedProduct.value?.date ?? moment().format("YYYY-MM-DD"),
            },
          });
          return;
        }
      }
      await store.dispatch("setOrderBusinessClient", client);
      router.push({
        name: "productPriceLines",
        params: {
          productId: props.productId,
          slotId: selectedProduct.value?.slotId,
          dateStart:
            selectedProduct.value?.date ?? moment().format("YYYY-MM-DD"),
        },
      });
    }
    return { setClient, clientsToShow, searchText };
  },
});
